<template>
	<Card
		class="ProgrammeCard"
		:featured="programme?.isFeatured"
		:loading="loading"
		@click="handleCardClick"
		@visible="handleCardVisible"
	>
		<ProgrammeHeader
			:organizationName="programme?.organizationName"
			:organizationLogoUrl="programme?.organizationLogoUrl"
			:programmeId="programme?.id"
			:reviewRating="programme?.reviewRating"
			:ranking="programme?.ranking"
			:locations="programme?.locations"
			:showLogo="programme?.isFeatured"
			:loading="loading"
			:externalServicesLoading="externalServicesLoading"
		/>

		<ProgrammeDetails
			:programmeTitle="programme?.title"
			:summary="programme?.summary"
			:loading="loading"
		/>

		<ProgrammeFacts
			:facts="programme?.standardFacts"
			:highlightedFacts="programme?.highlightedFacts"
			:loading="loading"
		/>

		<ProgrammePrice
			class="ProgrammePrice"
			:tuition="programme?.tuition ?? null"
			:duration="programme?.duration || ''"
			:loading="loading"
		/>

		<template #right-footer>
			<a
				v-skeleton="{
					loading,
					sizes: {
						Small: { width: '10.75rem', height: '0.75rem' },
						ExtraLarge: { width: '12.563rem', height: '1.75rem' },

					}
				}"
				class="VisitProgramme"
				:href="programme?.url"
				:target="isMobile ? '_self' : '_blank'"
				data-allow-mismatch="attribute"
			>
				View Programme Information
			</a>
		</template>
	</Card>
</template>

<script setup lang="ts">
import { type PropType } from 'vue';
import type IPersonalizedProgrammeCard from '@shared/interfaces/dtos/IPersonalizedProgrammeCard';
import Card from '@components/SearchResults/Card/Card.vue';
import ProgrammeHeader from './ProgrammeHeader/ProgrammeHeader.vue';
import ProgrammeDetails from './ProgrammeDetails/ProgrammeDetails.vue';
import ProgrammeFacts from './ProgrammeFacts/ProgrammeFacts.vue';
import ProgrammePrice from './ProgrammePrice/ProgrammePrice.vue';
import { useMobile } from '@composables/useMobile';
import { TrackingService } from '@domain/services/TrackingService';
const { isMobile } = useMobile();
const trackingService = TrackingService.getInstance();

const props = defineProps({
	programme: {
		type: Object as PropType<IPersonalizedProgrammeCard>,
		required: false,
	},
	position: {
		type: Number,
		required: true,
	},
	loading: {
		type: Boolean,
		default: true,
	},
	externalServicesLoading: {
		type: Boolean,
		default: false,
	},
});

const handleCardClick = () => {
	if (!props.programme) return;
	if (!props.programme.url) return;

	trackingService.trackCardClick(
		Number(props.programme.id),
		props.programme.organizationId,
		props.position + 1
	);


	//This "trick" avoids changing page before the tracking is fully executed, which could cause the tracking to be lost in some cases.
	setTimeout(() => {
		window.open(`${props.programme?.url}?ref=search_card`, isMobile.value ? '_self' : '_blank');
	}, 0);
};

const handleCardVisible = () => {
	if (!props.programme) return;

	trackingService.trackCardImpression(
		Number(props.programme.id),
		props.programme.organizationId,
		props.position + 1
	);
};

</script>

<style scoped lang="scss">
	.ProgrammeCard {
		&.Card {
			display: grid;
			grid-template-columns: minmax(0, 1fr);
			grid-template-areas:
				'header'
				'details'
				'facts'
				'price'
				'footer';
		}

		cursor: pointer;

		.ProgrammeHeader {
			grid-area: header;
		}

		.ProgrammeDetails {
			grid-area: details;
		}

		.ProgrammeFacts {
			grid-area: facts;
		}

		.ProgrammePrice {
			grid-area: price;
		}

		:deep(.Footer) {
			grid-area: footer;
		}

		.VisitProgramme {
			color: $Blue-700;
			text-decoration: none;

			@include TextStyle(UiSmallSemibold);

			&:hover {
				text-decoration: underline;
			}

			@include Breakpoint(Large, ExtraLarge) {
				@include TextStyle(UiMediumSemibold);
			}
		}

		@include Breakpoint(Large, ExtraLarge) {
			&.Card {
				grid-template-columns: minmax(0, 1fr) auto;
				grid-template-areas:
					'header header'
					'details details'
					'facts price'
					'footer footer';
			}
		}
	}
</style>
